import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HeartPulse } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "patient" ? "/assistant" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/38554812/pexels-photo-38554812.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt="Hospital lobby" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0D5C75]/70" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <HeartPulse size={40} />
          <h2 className="font-heading text-4xl font-extrabold mt-4">SarvaSeva</h2>
          <p className="text-white/85 mt-2 text-lg">Accredited care, coordinated end-to-end, protected by escrow.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8 text-[#0D5C75] font-heading font-extrabold text-lg">
            <HeartPulse /> SarvaSeva
          </Link>
          <h1 className="font-heading text-3xl font-bold text-[#101828]">Sign in</h1>
          <p className="text-muted-foreground mt-1">Welcome back to your health journey.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 focus:ring-2 focus:ring-[#2A9D8F]" required data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 focus:ring-2 focus:ring-[#2A9D8F]" required data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading}
              className="w-full h-11 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white"
              data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            New here? <Link to="/register" className="text-[#0D5C75] font-medium hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
